/**
 * MovimientoAdmin.js - Correcciones de movimientos bancarios (solo superadmin).
 *
 * Eliminar, editar y crear movimientos a mano, más el detector de posibles
 * duplicados y la bitácora de ajustes. El backend (banco_admin_views.py)
 * ajusta el saldo de la cuenta en la misma operación, bloquea lo que está en
 * una conciliación cerrada y registra cada cambio con la foto de antes/después.
 */
import { useState, useEffect, useCallback } from "react";
import apiFetch from "../../../../shared/services/apiClient";
import { formatCurrency } from "../../../../shared/utils/formatCurrency";
import { parseMoney, sanitizeMoneyTyping } from "../../../../shared/utils/parseMoney";
import { overlayClose } from "../../../../shared/utils/overlayClose";

const API = "/api/finance/bancoMovimiento-admin";
const SIMBOLO = { CRC: "₡", USD: "$", EUR: "€" };

export function fmtDate(iso) {
  if (!iso || iso.length < 10) return iso ?? "";
  const [y, m, d] = iso.slice(0, 10).split("-");
  return `${d}/${m}/${y}`;
}

function fmtDateTime(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  return `${fmtDate(iso)} ${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
}

async function leerError(res) {
  const d = await res.json().catch(() => ({}));
  if (d?.detail) return d.detail;
  if (typeof d === "object" && d) {
    const primero = Object.values(d)[0];
    if (primero) return Array.isArray(primero) ? primero[0] : String(primero);
  }
  return `Error ${res.status}`;
}

/* ── estilos ─────────────────────────────────────────────────────────────── */
const inputSt = {
  background: "var(--bg-input, var(--bg))", border: "1px solid var(--border-input, var(--border-card))",
  borderRadius: 8, padding: "9px 12px", color: "var(--text-primary)",
  fontSize: "0.85rem", outline: "none", width: "100%", boxSizing: "border-box",
};
const labelSt = { display: "block", fontSize: "0.74rem", fontWeight: 700, color: "var(--text-muted)", marginBottom: 5 };

function Btn({ children, onClick, variant = "primary", disabled, small, title }) {
  const cfg = {
    primary: { bg: "#f97316", color: "#fff", border: "#f97316" },
    ghost:   { bg: "transparent", color: "var(--text-muted)", border: "var(--border-card)" },
    danger:  { bg: "#ef4444", color: "#fff", border: "#ef4444" },
    soft:    { bg: "rgba(249,115,22,0.1)", color: "#f97316", border: "rgba(249,115,22,0.35)" },
  }[variant];
  return (
    <button type="button" onClick={onClick} disabled={disabled} title={title}
      style={{ background: cfg.bg, border: `1px solid ${cfg.border}`, borderRadius: 8, color: cfg.color,
        padding: small ? "6px 12px" : "9px 18px", fontSize: small ? "0.78rem" : "0.85rem", fontWeight: 600,
        cursor: disabled ? "not-allowed" : "pointer", opacity: disabled ? 0.5 : 1,
        display: "inline-flex", alignItems: "center", gap: 7, whiteSpace: "nowrap" }}>
      {children}
    </button>
  );
}

export function IconBtn({ icon, title, onClick, color = "var(--text-muted)" }) {
  return (
    <button type="button" onClick={onClick} title={title}
      style={{ width: 28, height: 28, borderRadius: 7, border: "1px solid var(--border-card)",
        background: "transparent", color, cursor: "pointer", display: "inline-flex",
        alignItems: "center", justifyContent: "center", fontSize: "0.74rem" }}>
      <i className={`fa-solid ${icon}`} />
    </button>
  );
}

function Shell({ onClose, icon, iconColor = "#f97316", title, subtitle, width = 560, children, footer }) {
  return (
    <div {...overlayClose(onClose)} style={{
      position: "fixed", inset: 0, zIndex: 1000, background: "rgba(0,0,0,0.66)", backdropFilter: "blur(3px)",
      display: "flex", alignItems: "center", justifyContent: "center", padding: 16,
    }}>
      <div onClick={e => e.stopPropagation()} style={{
        background: "var(--bg-card)", border: "1px solid var(--border-card)", borderRadius: 16,
        width: `min(${width}px, 96vw)`, maxHeight: "90vh", display: "flex", flexDirection: "column",
        boxShadow: "0 28px 70px rgba(0,0,0,0.5)",
      }}>
        <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between",
          padding: "16px 20px", borderBottom: "1px solid var(--border-card)", gap: 10 }}>
          <div style={{ display: "flex", gap: 12, alignItems: "flex-start", minWidth: 0 }}>
            <div style={{ width: 38, height: 38, borderRadius: 10, flexShrink: 0,
              background: `${iconColor}18`, border: `1px solid ${iconColor}30`,
              display: "flex", alignItems: "center", justifyContent: "center" }}>
              <i className={`fa-solid ${icon}`} style={{ color: iconColor }} />
            </div>
            <div style={{ minWidth: 0 }}>
              <h2 style={{ margin: 0, fontSize: "1rem", fontWeight: 700, color: "var(--text-primary)" }}>{title}</h2>
              {subtitle && <p style={{ margin: "3px 0 0", color: "var(--text-muted)", fontSize: "0.78rem" }}>{subtitle}</p>}
            </div>
          </div>
          <button type="button" onClick={onClose} style={{ background: "none", border: "none",
            color: "var(--text-muted)", cursor: "pointer", fontSize: "1.05rem", padding: 4 }}>
            <i className="fa-solid fa-xmark" />
          </button>
        </div>
        <div style={{ padding: 20, overflow: "auto" }}>{children}</div>
        {footer && (
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, flexWrap: "wrap",
            padding: "12px 20px", borderTop: "1px solid var(--border-card)" }}>
            {footer}
          </div>
        )}
      </div>
    </div>
  );
}

function Aviso({ tipo = "error", icono, children }) {
  const c = { error: "#ef4444", warn: "#f59e0b", info: "#3b82f6", ok: "#22c55e" }[tipo];
  const icon = icono || { error: "fa-triangle-exclamation", warn: "fa-triangle-exclamation", info: "fa-circle-info", ok: "fa-circle-check" }[tipo];
  return (
    <div style={{ display: "flex", gap: 10, alignItems: "flex-start", background: `${c}14`,
      border: `1px solid ${c}40`, borderLeft: `3px solid ${c}`, borderRadius: 8,
      padding: "9px 13px", marginBottom: 12, fontSize: "0.81rem", lineHeight: 1.5, color: "var(--text-primary)" }}>
      <i className={`fa-solid ${icon}`} style={{ color: c, marginTop: 3, flexShrink: 0 }} />
      <div style={{ flex: 1 }}>{children}</div>
    </div>
  );
}

function Dato({ label, children }) {
  return (
    <div style={{ minWidth: 0 }}>
      <div style={{ fontSize: "0.68rem", fontWeight: 700, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: 0.3 }}>{label}</div>
      <div style={{ fontSize: "0.85rem", color: "var(--text-primary)", marginTop: 2, wordBreak: "break-word" }}>{children || "-"}</div>
    </div>
  );
}

function Tarjeta({ titulo, icon, color = "#f97316", children }) {
  return (
    <div style={{ border: "1px solid var(--border-card)", borderRadius: 10, padding: "12px 14px", marginBottom: 12 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10,
        fontSize: "0.78rem", fontWeight: 700, color }}>
        <i className={`fa-solid ${icon}`} />{titulo}
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(150px, 1fr))", gap: "10px 16px" }}>
        {children}
      </div>
    </div>
  );
}

function montoMov(mov, moneda) {
  const s = SIMBOLO[moneda] || "₡";
  const deb = Number(mov.debito) || 0;
  const cre = Number(mov.credito) || 0;
  if (cre > 0) return <span style={{ color: "#22c55e", fontWeight: 700 }}>+{s}{formatCurrency(cre)} (crédito)</span>;
  if (deb > 0) return <span style={{ color: "#ef4444", fontWeight: 700 }}>-{s}{formatCurrency(deb)} (débito)</span>;
  return "-";
}

function TarjetaRecibo({ r, titulo = "Recibo vinculado" }) {
  if (!r) return null;
  const s = SIMBOLO[r.moneda] || "₡";
  return (
    <Tarjeta titulo={`${titulo}: ${r.numero}`} icon="fa-receipt" color="#3b82f6">
      <Dato label={r.tipo === "CXP" ? "Proveedor" : "Propietario"}>{r.persona}</Dato>
      {r.tipo === "CXC" && <Dato label="Filial">{r.filial}</Dato>}
      <Dato label="Fecha de pago">{fmtDate(r.fecha)}</Dato>
      <Dato label="Monto del recibo">{s}{formatCurrency(r.monto)}</Dato>
      <Dato label="Método">{r.metodo}</Dato>
      <Dato label="Documento">{r.documento}</Dato>
    </Tarjeta>
  );
}

/** Movimiento + recibo + conciliación: lo que se ve antes de tocar nada. */
function ContextoMovimiento({ ctx }) {
  const mov = ctx.movimiento;
  return (
    <>
      <Tarjeta titulo={`Movimiento #${mov.id}`} icon="fa-building-columns">
        <Dato label="Cuenta">{ctx.cuenta.banco} - {ctx.cuenta.numero} ({ctx.cuenta.moneda})</Dato>
        <Dato label="Fecha">{fmtDate(mov.fecha)}</Dato>
        <Dato label="Documento">{mov.documento}</Dato>
        <Dato label="Monto">{montoMov(mov, ctx.cuenta.moneda)}</Dato>
        <div style={{ gridColumn: "1 / -1" }}><Dato label="Descripción">{mov.descripcion}</Dato></div>
      </Tarjeta>
      <TarjetaRecibo r={ctx.recibo || ctx.recibo_cxp} />
      {!ctx.recibo && !ctx.recibo_cxp && (
        ctx.recibo_sugerido ? (
          <Aviso tipo="info">
            No está vinculado, pero su descripción menciona el recibo <strong>{ctx.recibo_sugerido.numero}</strong>
            {ctx.recibo_sugerido.persona ? <> de <strong>{ctx.recibo_sugerido.persona}</strong></> : null}
            {ctx.recibo_sugerido.filial ? <> (filial {ctx.recibo_sugerido.filial})</> : null}.
            {ctx.recibo_sugerido.es_su_movimiento === false &&
              <> Ese recibo ya tiene otro movimiento: este podría ser el duplicado.</>}
          </Aviso>
        ) : (
          <Aviso tipo="info">Este movimiento no está vinculado a ningún recibo ni pago.</Aviso>
        )
      )}
      {ctx.conciliacion && (
        <Aviso tipo={ctx.bloqueado ? "warn" : "info"} icono={ctx.bloqueado ? "fa-lock" : undefined}>
          Marcado en la conciliación con corte al {fmtDate(ctx.conciliacion.fecha_corte)} ({ctx.conciliacion.estado}).
          {ctx.bloqueado && <> Mientras esté cerrada no se puede eliminar ni cambiar su monto, fecha o cuenta.</>}
        </Aviso>
      )}
    </>
  );
}

function useContexto(movId) {
  const [ctx, setCtx] = useState(null);
  const [err, setErr] = useState("");
  useEffect(() => {
    let vivo = true;
    apiFetch(`${API}/${movId}/contexto/`)
      .then(async r => { if (!r.ok) throw new Error(await leerError(r)); return r.json(); })
      .then(d => vivo && setCtx(d))
      .catch(e => vivo && setErr(e.message));
    return () => { vivo = false; };
  }, [movId]);
  return [ctx, err];
}

function Cargando() {
  return (
    <div style={{ textAlign: "center", padding: 28, color: "var(--text-muted)" }}>
      <i className="fa-solid fa-circle-notch fa-spin" style={{ color: "#f97316", marginRight: 8 }} />Cargando...
    </div>
  );
}

function CampoMotivo({ value, onChange, placeholder }) {
  return (
    <div style={{ marginTop: 4 }}>
      <label style={labelSt}>Motivo (queda en la bitácora)</label>
      <textarea rows={2} style={{ ...inputSt, resize: "vertical" }} value={value}
        placeholder={placeholder} onChange={e => onChange(e.target.value)} />
    </div>
  );
}

/* ═══════════════════════ Eliminar ═══════════════════════ */
export function EliminarMovimientoModal({ movId, onClose, onDone }) {
  const [ctx, errCtx] = useContexto(movId);
  const [motivo, setMotivo] = useState("");
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  async function eliminar() {
    setSaving(true); setErr("");
    try {
      const res = await apiFetch(`${API}/${movId}/`, { method: "DELETE", body: JSON.stringify({ motivo }) });
      if (!res.ok) throw new Error(await leerError(res));
      onDone("Movimiento eliminado. El saldo de la cuenta se ajustó.");
    } catch (e) { setErr(e.message); setSaving(false); }
  }

  const bloqueado = ctx?.bloqueado;
  const efecto = ctx ? (Number(ctx.movimiento.credito) || 0) - (Number(ctx.movimiento.debito) || 0) : 0;
  const s = SIMBOLO[ctx?.cuenta?.moneda] || "₡";

  return (
    <Shell onClose={onClose} icon="fa-trash" iconColor="#ef4444" title="Eliminar movimiento bancario"
      subtitle="Revisá a qué pertenece antes de eliminarlo"
      footer={<>
        <Btn variant="ghost" onClick={onClose} disabled={saving}>Cancelar</Btn>
        <Btn variant="danger" onClick={eliminar} disabled={saving || !ctx || bloqueado}>
          {saving ? <><i className="fa-solid fa-circle-notch fa-spin" />Eliminando...</> : <><i className="fa-solid fa-trash" />Sí, eliminar</>}
        </Btn>
      </>}>
      {errCtx && <Aviso>{errCtx}</Aviso>}
      {!ctx && !errCtx && <Cargando />}
      {ctx && (
        <>
          <ContextoMovimiento ctx={ctx} />
          {!bloqueado && (
            <Aviso tipo="warn">
              El saldo de <strong>{ctx.cuenta.banco} - {ctx.cuenta.numero}</strong> va a
              {efecto >= 0 ? " bajar " : " subir "}<strong>{s}{formatCurrency(Math.abs(efecto))}</strong>.
              No se tocan recibos, facturas ni asientos contables.
            </Aviso>
          )}
          <CampoMotivo value={motivo} onChange={setMotivo} placeholder="Ej: duplicado del R-00143" />
        </>
      )}
      {err && <div style={{ marginTop: 12 }}><Aviso>{err}</Aviso></div>}
    </Shell>
  );
}

/* ═══════════════════════ Formulario (crear / editar) ═══════════════════════ */
function FormMovimiento({ form, setForm, cuentas, bloqueado }) {
  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));
  const setMonto = (k) => (e) => setForm(f => ({ ...f, [k]: sanitizeMoneyTyping(e.target.value) }));
  const lock = bloqueado ? { disabled: true, title: "Conciliación cerrada" } : {};
  return (
    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 12 }}>
      <div style={{ gridColumn: "1 / -1" }}>
        <label style={labelSt}>Cuenta bancaria *</label>
        <select style={inputSt} value={form.cuenta_bancaria ?? ""} onChange={set("cuenta_bancaria")} {...lock}>
          <option value="">Seleccionar cuenta</option>
          {cuentas.map(c => <option key={c.id} value={c.id}>{c.banco} - {c.numero_cuenta} ({c.moneda})</option>)}
        </select>
      </div>
      <div>
        <label style={labelSt}>Fecha *</label>
        <input type="date" style={inputSt} value={form.fecha ?? ""} onChange={set("fecha")} {...lock} />
      </div>
      <div>
        <label style={labelSt}>Documento</label>
        <input style={inputSt} value={form.documento ?? ""} onChange={set("documento")} maxLength={100} />
      </div>
      <div>
        <label style={labelSt}>Débito (sale dinero)</label>
        <input style={inputSt} inputMode="decimal" value={form.debito ?? ""} onChange={setMonto("debito")} placeholder="0.00" {...lock} />
      </div>
      <div>
        <label style={labelSt}>Crédito (entra dinero)</label>
        <input style={inputSt} inputMode="decimal" value={form.credito ?? ""} onChange={setMonto("credito")} placeholder="0.00" {...lock} />
      </div>
      <div style={{ gridColumn: "1 / -1" }}>
        <label style={labelSt}>Descripción</label>
        <input style={inputSt} value={form.descripcion ?? ""} onChange={set("descripcion")} />
      </div>
    </div>
  );
}

/** Buscar un recibo por número (R-00143 / RP-00012). */
function BuscadorRecibo({ onEncontrado, placeholder = "R-00143 o RP-00012", boton = "Buscar" }) {
  const [numero, setNumero] = useState("");
  const [buscando, setBuscando] = useState(false);
  const [err, setErr] = useState("");
  async function buscar() {
    if (!numero.trim()) return;
    setBuscando(true); setErr("");
    try {
      const res = await apiFetch(`${API}/buscar-recibo/?numero=${encodeURIComponent(numero.trim())}`);
      if (!res.ok) throw new Error(await leerError(res));
      onEncontrado(await res.json());
    } catch (e) { setErr(e.message); } finally { setBuscando(false); }
  }
  return (
    <div style={{ marginBottom: 12 }}>
      <div style={{ display: "flex", gap: 8 }}>
        <input style={inputSt} value={numero} placeholder={placeholder}
          onChange={e => setNumero(e.target.value.toUpperCase())}
          onKeyDown={e => { if (e.key === "Enter") buscar(); }} />
        <Btn variant="soft" onClick={buscar} disabled={buscando || !numero.trim()}>
          <i className={`fa-solid ${buscando ? "fa-circle-notch fa-spin" : "fa-magnifying-glass"}`} />{boton}
        </Btn>
      </div>
      {err && <div style={{ color: "#ef4444", fontSize: "0.78rem", marginTop: 6 }}>{err}</div>}
    </div>
  );
}

function validar(form) {
  const deb = parseMoney(form.debito), cre = parseMoney(form.credito);
  if (!form.cuenta_bancaria) return "Elegí la cuenta bancaria.";
  if (!form.fecha) return "La fecha es obligatoria.";
  if ((deb > 0) === (cre > 0)) return "Indicá un débito o un crédito (uno solo, mayor a cero).";
  return "";
}

/* ═══════════════════════ Editar ═══════════════════════ */
export function EditarMovimientoModal({ movId, cuentas, onClose, onDone }) {
  const [ctx, errCtx] = useContexto(movId);
  const [form, setForm] = useState(null);
  const [vinculo, setVinculo] = useState(null);   // {tipo, id, info} | null
  const [cambioVinculo, setCambioVinculo] = useState(false);
  const [motivo, setMotivo] = useState("");
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (!ctx) return;
    const m = ctx.movimiento;
    setForm({
      cuenta_bancaria: String(m.cuenta_bancaria), fecha: m.fecha, documento: m.documento || "",
      descripcion: m.descripcion || "",
      debito: Number(m.debito) ? m.debito : "", credito: Number(m.credito) ? m.credito : "",
    });
    const r = ctx.recibo || ctx.recibo_cxp;
    setVinculo(r ? { tipo: r.tipo, id: r.id, info: r } : null);
  }, [ctx]);

  async function guardar() {
    const v = validar(form);
    if (v) { setErr(v); return; }
    setSaving(true); setErr("");
    const m = ctx.movimiento;
    const body = { motivo };
    if (String(form.cuenta_bancaria) !== String(m.cuenta_bancaria)) body.cuenta_bancaria = Number(form.cuenta_bancaria);
    if (form.fecha !== m.fecha) body.fecha = form.fecha;
    if (parseMoney(form.debito) !== Number(m.debito)) body.debito = parseMoney(form.debito).toFixed(2);
    if (parseMoney(form.credito) !== Number(m.credito)) body.credito = parseMoney(form.credito).toFixed(2);
    if ((form.documento || "") !== (m.documento || "")) body.documento = form.documento;
    if ((form.descripcion || "") !== (m.descripcion || "")) body.descripcion = form.descripcion;
    if (cambioVinculo) {
      body.recibo = vinculo?.tipo === "CXC" ? vinculo.id : null;
      body.recibo_cxp = vinculo?.tipo === "CXP" ? vinculo.id : null;
      if (vinculo?.tipo === "CXC" && vinculo.filial) body.filial = vinculo.filial;
    }
    if (Object.keys(body).length === 1) { onClose(); return; }
    try {
      const res = await apiFetch(`${API}/${movId}/`, { method: "PATCH", body: JSON.stringify(body) });
      if (!res.ok) throw new Error(await leerError(res));
      onDone("Movimiento actualizado. El saldo de la cuenta se ajustó por la diferencia.");
    } catch (e) { setErr(e.message); setSaving(false); }
  }

  return (
    <Shell onClose={onClose} icon="fa-pen-to-square" title={`Editar movimiento #${movId}`}
      subtitle="Cambiar el monto, la fecha o la cuenta ajusta el saldo de la cuenta automáticamente"
      width={620}
      footer={<>
        <Btn variant="ghost" onClick={onClose} disabled={saving}>Cancelar</Btn>
        <Btn onClick={guardar} disabled={saving || !form}>
          {saving ? <><i className="fa-solid fa-circle-notch fa-spin" />Guardando...</> : <><i className="fa-solid fa-floppy-disk" />Guardar cambios</>}
        </Btn>
      </>}>
      {errCtx && <Aviso>{errCtx}</Aviso>}
      {!form && !errCtx && <Cargando />}
      {ctx && form && (
        <>
          {ctx.bloqueado && (
            <Aviso tipo="warn" icono="fa-lock">
              Está en una conciliación cerrada (corte {fmtDate(ctx.conciliacion.fecha_corte)}): solo se pueden
              cambiar el documento, la descripción y el recibo vinculado.
            </Aviso>
          )}
          <FormMovimiento form={form} setForm={setForm} cuentas={cuentas} bloqueado={ctx.bloqueado} />

          <label style={labelSt}>Recibo vinculado</label>
          {vinculo ? (
            <div style={{ display: "flex", alignItems: "center", gap: 10, border: "1px solid var(--border-card)",
              borderRadius: 8, padding: "8px 12px", marginBottom: 10 }}>
              <i className="fa-solid fa-link" style={{ color: "#3b82f6" }} />
              <div style={{ flex: 1, fontSize: "0.83rem", color: "var(--text-primary)" }}>
                <strong>{vinculo.info.numero}</strong>
                {vinculo.info.persona ? ` - ${vinculo.info.persona}` : ""}
                {vinculo.info.filial ? ` (filial ${vinculo.info.filial})` : ""}
              </div>
              <Btn small variant="ghost" onClick={() => { setVinculo(null); setCambioVinculo(true); }}>
                <i className="fa-solid fa-link-slash" />Desvincular
              </Btn>
            </div>
          ) : (
            <BuscadorRecibo boton="Vincular" onEncontrado={d => {
              setVinculo({ tipo: d.recibo.tipo, id: d.recibo.id, info: d.recibo, filial: d.sugerencia.filial });
              setCambioVinculo(true);
            }} />
          )}
          <CampoMotivo value={motivo} onChange={setMotivo} placeholder="Ej: monto mal digitado en el recibo" />
        </>
      )}
      {err && <div style={{ marginTop: 12 }}><Aviso>{err}</Aviso></div>}
    </Shell>
  );
}

/* ═══════════════════════ Crear ═══════════════════════ */
const FORM_VACIO = { cuenta_bancaria: "", fecha: "", documento: "", descripcion: "", debito: "", credito: "" };

export function CrearMovimientoModal({ cuentas, onClose, onDone }) {
  const [modo, setModo] = useState("recibo");     // "recibo" | "manual"
  const [hallazgo, setHallazgo] = useState(null); // respuesta de buscar-recibo
  const [form, setForm] = useState(FORM_VACIO);
  const [motivo, setMotivo] = useState("");
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  function usarRecibo(d) {
    setHallazgo(d);
    const s = d.sugerencia;
    setForm({
      cuenta_bancaria: s.cuenta_bancaria ? String(s.cuenta_bancaria) : "", fecha: s.fecha || "",
      documento: s.documento || "", descripcion: s.descripcion || "",
      debito: Number(s.debito) ? s.debito : "", credito: Number(s.credito) ? s.credito : "",
    });
    setErr("");
  }

  async function crear() {
    const v = validar(form);
    if (v) { setErr(v); return; }
    setSaving(true); setErr("");
    const body = {
      ...form, cuenta_bancaria: Number(form.cuenta_bancaria),
      debito: parseMoney(form.debito).toFixed(2), credito: parseMoney(form.credito).toFixed(2), motivo,
    };
    if (modo === "recibo" && hallazgo) {
      const s = hallazgo.sugerencia;
      if (s.recibo) body.recibo = s.recibo;
      if (s.recibo_cxp) body.recibo_cxp = s.recibo_cxp;
      if (s.filial) body.filial = s.filial;
    }
    try {
      const res = await apiFetch(`${API}/`, { method: "POST", body: JSON.stringify(body) });
      if (!res.ok) throw new Error(await leerError(res));
      onDone("Movimiento creado. El saldo de la cuenta se ajustó.");
    } catch (e) { setErr(e.message); setSaving(false); }
  }

  const existentes = hallazgo?.movimientos_existentes || [];
  const campoVinculo = hallazgo?.recibo?.tipo === "CXP" ? "recibo_cxp" : "recibo";
  const vinculadoYa = !!hallazgo && existentes.some(m => m[campoVinculo] === hallazgo.recibo.id);
  const listo = modo === "manual" || !!hallazgo;

  const tab = (clave, label, icon) => (
    <button type="button" onClick={() => { setModo(clave); setErr(""); if (clave === "manual") { setHallazgo(null); setForm(FORM_VACIO); } }}
      style={{ flex: 1, padding: "8px 10px", borderRadius: 8, cursor: "pointer", fontSize: "0.8rem", fontWeight: 700,
        border: `1px solid ${modo === clave ? "rgba(249,115,22,0.5)" : "var(--border-card)"}`,
        background: modo === clave ? "rgba(249,115,22,0.1)" : "transparent",
        color: modo === clave ? "#f97316" : "var(--text-muted)" }}>
      <i className={`fa-solid ${icon}`} style={{ marginRight: 6 }} />{label}
    </button>
  );

  return (
    <Shell onClose={onClose} icon="fa-plus" title="Crear movimiento bancario"
      subtitle="Para un recibo cuyo movimiento no se creó, o un ajuste manual" width={640}
      footer={<>
        <Btn variant="ghost" onClick={onClose} disabled={saving}>Cancelar</Btn>
        <Btn onClick={crear} disabled={saving || !listo || vinculadoYa}>
          {saving ? <><i className="fa-solid fa-circle-notch fa-spin" />Creando...</> : <><i className="fa-solid fa-plus" />Crear movimiento</>}
        </Btn>
      </>}>
      <div style={{ display: "flex", gap: 8, marginBottom: 14 }}>
        {tab("recibo", "Para un recibo", "fa-receipt")}
        {tab("manual", "Sin recibo", "fa-pen")}
      </div>

      {modo === "recibo" && <BuscadorRecibo onEncontrado={usarRecibo} />}

      {modo === "recibo" && hallazgo && (
        <>
          <TarjetaRecibo r={hallazgo.recibo} titulo="Recibo" />
          {!hallazgo.recibo.cuenta_bancaria && (
            <Aviso tipo="warn">El recibo no tiene cuenta bancaria (pago en efectivo o sin banco): elegí la cuenta a mano.</Aviso>
          )}
          {existentes.length > 0 ? (
            <Aviso tipo={vinculadoYa ? "error" : "warn"}>
              {vinculadoYa
                ? "Este recibo ya tiene su movimiento vinculado. Si está mal, editalo en vez de crear otro."
                : "Ya hay movimientos que mencionan este recibo. Revisá que no sea uno de estos antes de crear otro:"}
              <ul style={{ margin: "6px 0 0", paddingLeft: 18 }}>
                {existentes.map(m => (
                  <li key={m.id}>#{m.id} - {fmtDate(m.fecha)} - {m.documento || "sin documento"} -
                    {" "}{Number(m.credito) ? `crédito ${formatCurrency(m.credito)}` : `débito ${formatCurrency(m.debito)}`}</li>
                ))}
              </ul>
            </Aviso>
          ) : (
            <Aviso tipo="ok">
              No hay ningún movimiento de este recibo. Monto calculado igual que lo haría el sistema
              (lo aplicado, en la moneda de la cuenta): <strong>{formatCurrency(hallazgo.monto_esperado)}</strong>.
            </Aviso>
          )}
        </>
      )}

      {listo && <FormMovimiento form={form} setForm={setForm} cuentas={cuentas} />}
      {listo && <CampoMotivo value={motivo} onChange={setMotivo} placeholder="Ej: el recibo R-00143 no generó su movimiento" />}
      {err && <div style={{ marginTop: 12 }}><Aviso>{err}</Aviso></div>}
    </Shell>
  );
}

/* ═══════════════════════ Posibles duplicados ═══════════════════════ */
export function DuplicadosModal({ onClose, onEliminar, onEditar, version }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");

  const cargar = useCallback(() => {
    setData(null); setErr("");
    apiFetch(`${API}/duplicados/`)
      .then(async r => { if (!r.ok) throw new Error(await leerError(r)); return r.json(); })
      .then(setData)
      .catch(e => setErr(e.message));
  }, []);

  useEffect(() => { cargar(); }, [cargar, version]);

  return (
    <Shell onClose={onClose} icon="fa-clone" iconColor="#f59e0b" title="Posibles duplicados"
      subtitle="Misma cuenta, fecha, documento y monto, sin un recibo distinto que justifique cada uno" width={760}
      footer={<Btn variant="ghost" onClick={onClose}>Cerrar</Btn>}>
      {err && <Aviso>{err}</Aviso>}
      {!data && !err && <Cargando />}
      {data && data.total === 0 && (
        <Aviso tipo="ok">No se encontraron movimientos duplicados en este condominio.</Aviso>
      )}
      {data && data.grupos.map((g, i) => (
        <div key={i} style={{ border: "1px solid var(--border-card)", borderRadius: 10, marginBottom: 12, overflow: "hidden" }}>
          <div style={{ display: "flex", flexWrap: "wrap", gap: "4px 16px", padding: "9px 14px",
            background: "rgba(245,158,11,0.08)", fontSize: "0.8rem", color: "var(--text-primary)" }}>
            <strong>{fmtDate(g.fecha)}</strong>
            <span>{g.cuenta}</span>
            <span>Doc. {g.documento || "-"}</span>
            <span style={{ fontWeight: 700, color: g.tipo === "credito" ? "#22c55e" : "#ef4444" }}>
              {SIMBOLO[g.moneda] || "₡"}{formatCurrency(g.monto)}
            </span>
            <span style={{ color: "var(--text-muted)" }}>{g.movimientos.length} movimientos</span>
          </div>
          {g.movimientos.map(m => (
            <div key={m.id} style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 14px",
              borderTop: "1px solid var(--border-card)", fontSize: "0.8rem" }}>
              <span style={{ color: "var(--text-muted)", width: 48, flexShrink: 0 }}>#{m.id}</span>
              <span style={{ flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                color: "var(--text-primary)" }} title={m.descripcion}>{m.descripcion || "-"}</span>
              {m.vinculado && <Etiqueta color="#3b82f6" icon="fa-link">Vinculado</Etiqueta>}
              {m.sin_recibo && <Etiqueta color="#f59e0b" icon="fa-circle-question">Sin recibo</Etiqueta>}
              {m.conciliado && <Etiqueta color="#22c55e" icon="fa-check">{m.bloqueado ? "Conc. cerrada" : "Conciliado"}</Etiqueta>}
              <IconBtn icon="fa-pen" title="Editar" onClick={() => onEditar(m.id)} />
              <IconBtn icon="fa-trash" title="Eliminar" color="#ef4444" onClick={() => onEliminar(m.id)} />
            </div>
          ))}
        </div>
      ))}
    </Shell>
  );
}

function Etiqueta({ color, icon, children }) {
  return (
    <span style={{ padding: "1px 7px", borderRadius: 5, fontSize: "0.68rem", fontWeight: 700,
      background: `${color}1f`, color, whiteSpace: "nowrap", flexShrink: 0 }}>
      <i className={`fa-solid ${icon}`} style={{ marginRight: 4 }} />{children}
    </span>
  );
}

/* ═══════════════════════ Bitácora ═══════════════════════ */
const ACCION = {
  CREAR:    { label: "Creado",    color: "#22c55e", icon: "fa-plus" },
  EDITAR:   { label: "Editado",   color: "#3b82f6", icon: "fa-pen" },
  ELIMINAR: { label: "Eliminado", color: "#ef4444", icon: "fa-trash" },
};
const CAMPOS = { fecha: "Fecha", documento: "Documento", descripcion: "Descripción", debito: "Débito",
  credito: "Crédito", cuenta_bancaria: "Cuenta", recibo: "Recibo CxC", recibo_cxp: "Pago CxP", filial: "Filial" };

function resumenFoto(f) {
  if (!f) return "";
  const monto = Number(f.credito) ? `crédito ${formatCurrency(f.credito)}` : `débito ${formatCurrency(f.debito)}`;
  return `${fmtDate(f.fecha)} - doc. ${f.documento || "-"} - ${monto} - ${f.descripcion || ""}`;
}

export function BitacoraModal({ cuentas = [], onClose }) {
  const valor = (k, v) => {
    if (v == null || v === "") return "-";
    if (k === "cuenta_bancaria") {
      const c = cuentas.find(x => String(x.id) === String(v));
      return c ? `${c.banco} - ${c.numero_cuenta}` : `#${v}`;
    }
    if (k === "recibo" || k === "recibo_cxp" || k === "filial") return `#${v}`;
    return String(v);
  };
  const [items, setItems] = useState(null);
  const [err, setErr] = useState("");
  useEffect(() => {
    apiFetch(`${API}/bitacora/`)
      .then(async r => { if (!r.ok) throw new Error(await leerError(r)); return r.json(); })
      .then(setItems)
      .catch(e => setErr(e.message));
  }, []);

  return (
    <Shell onClose={onClose} icon="fa-clock-rotate-left" iconColor="#8b5cf6" title="Bitácora de ajustes"
      subtitle="Cada movimiento creado, editado o eliminado a mano, con quién y por qué" width={720}
      footer={<Btn variant="ghost" onClick={onClose}>Cerrar</Btn>}>
      {err && <Aviso>{err}</Aviso>}
      {!items && !err && <Cargando />}
      {items && items.length === 0 && <Aviso tipo="info">Todavía no hay ajustes registrados.</Aviso>}
      {items && items.map(a => {
        const cfg = ACCION[a.accion] || ACCION.EDITAR;
        const cambios = a.antes && a.despues
          ? Object.keys(CAMPOS).filter(k => String(a.antes[k] ?? "") !== String(a.despues[k] ?? ""))
          : [];
        return (
          <div key={a.id} style={{ borderLeft: `3px solid ${cfg.color}`, padding: "8px 12px", marginBottom: 10,
            background: "var(--bg-input, transparent)", borderRadius: 6, fontSize: "0.8rem", color: "var(--text-primary)" }}>
            <div style={{ display: "flex", flexWrap: "wrap", gap: "2px 12px", alignItems: "center" }}>
              <span style={{ color: cfg.color, fontWeight: 700 }}>
                <i className={`fa-solid ${cfg.icon}`} style={{ marginRight: 5 }} />{cfg.label} #{a.movimiento_id}
              </span>
              <span style={{ color: "var(--text-muted)" }}>{fmtDateTime(a.fecha)}</span>
              <span style={{ color: "var(--text-muted)" }}><i className="fa-solid fa-user" style={{ marginRight: 4 }} />{a.usuario || "-"}</span>
            </div>
            {a.accion !== "EDITAR" && <div style={{ marginTop: 4 }}>{resumenFoto(a.antes || a.despues)}</div>}
            {cambios.map(k => (
              <div key={k} style={{ marginTop: 3 }}>
                <span style={{ color: "var(--text-muted)" }}>{CAMPOS[k]}:</span>{" "}
                <span style={{ textDecoration: "line-through", opacity: 0.7 }}>{valor(k, a.antes[k])}</span>
                {" "}<i className="fa-solid fa-arrow-right" style={{ fontSize: "0.65rem", margin: "0 4px" }} />
                <strong>{valor(k, a.despues[k])}</strong>
              </div>
            ))}
            {a.motivo && <div style={{ marginTop: 4, fontStyle: "italic", color: "var(--text-muted)" }}>"{a.motivo}"</div>}
          </div>
        );
      })}
    </Shell>
  );
}
